import { ReactElement, ReactNode, useState, useEffect } from "react";
import { SidebarHeader } from "src/components/ui/sidebar";
import { Skeleton } from "src/components/ui/skeleton";
import useShareSurvey from "src/hooks/useShareSurvey";
import { SurveyFull, SurveyStatus } from "src/redux/survey/survey.types";
import { useViewSurveyChildren, ViewSurveyChildrenProps } from "../useViewSurveyChildren";
import ResultsSidebarHeaderDownload, {ResultsSidebarHeaderDownloadProps } from "./results-sidebar-header-download";
import ResultsSidebarHeaderShare, {ResultsSidebarHeaderShareProps } from "./results-sidebar-header-share";
import { useNavigate } from "react-router-dom";
// import ResultsSidebarHeaderOptions from "./results-sidebar-header-options";
import SurveyResponderTrigger from "src/components/criteria/survey-responder-trigger";
import { ReactComponent as PencilIcon } from "src/assets/icons-redesign/pencil.svg";
import { ReactComponent as CheckIcon } from "src/assets/icons-redesign/check.svg";
import { useUpdateSurveyMutation } from "src/redux/survey/survey.endpoints";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "src/redux/notification/notification.slice";
import ErrorLogService from "src/services/error-log/error-log.service";
import { hasSurveyAIPanelAccess } from "src/services/permission/permission.helpers";
import { getIsUserOrg, getUserStateActiveClient } from "src/redux/user/user.selectors";

interface LayoutProps {
  download: ReactNode;
  onBack: () => void;
  share: ReactNode;
  title: ReactElement;
  survey?: SurveyFull | undefined;
  // complete: ReactNode;
  createSummary?: () => void;
  createMediaPlan?: () => void;
  isShare: boolean;
}

const Layout = ({ download, onBack, share, title, survey, createSummary, createMediaPlan, isShare }: LayoutProps) => {
  const client = useSelector(getUserStateActiveClient);
  const isOrgUser = useSelector(getIsUserOrg);
  const [canShowAIPanel, setCanShowAIPanel] = useState(false);

  // The AI Panel should only show if:
  // - it's NOT a share view (isShare === false)
  // - the current user/client has AI Panel access (hasSurveyAIPanelAccess returns true)
  useEffect(() => {
    if (isOrgUser) {
      setCanShowAIPanel(true);
    } else {
      setCanShowAIPanel(!isShare && hasSurveyAIPanelAccess({ permissions: client?.permissions }));
    }
  }, [isShare, client, isOrgUser]);

  return (
    <SidebarHeader className="p-6 gap-4">
      <div className="flex gap-2">
        {/* <Button
          title="Go to dashboard"
          onClick={onBack}
          variant="outline"
          size="icon"
          className='[&_svg]:size-5'
        >
          <BackArrow />
        </Button> */}
        {/* <div className="ml-auto">
          <ResultsSidebarHeaderOptions
            survey={survey}
            createSummary={createSummary}
            createMediaPlan={createMediaPlan}
          />
        </div> */}
      </div>
      {title}
      <div className="flex flex-row gap-2">
        {download}
        {share}
        {canShowAIPanel && <SurveyResponderTrigger extSurveyId={survey ? survey?.extSurveyId : ''} />}
        {/* {complete} */}
        <div className="ml-auto">
          {/* <ResultsSidebarHeaderOptions survey={survey} /> */}
        </div>
      </div>
    </SidebarHeader>
  );
};

export const SurveyResultsSidebarHeader = ({ isShare, surveyId, createSummary, createMediaPlan }: ViewSurveyChildrenProps): ReactElement => {
  const { isSurveyError, survey } = useViewSurveyChildren(surveyId, isShare);
  const { handleShare, isLoading, canShare } = useShareSurvey({ surveyId, surveyStatus: survey?.surveyStatus });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [updateSurvey, { error: updateError, isError: isUpdateError }] = useUpdateSurveyMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    if (isUpdateError) {
      dispatch(showNotification({
        theme: 'warning',
        summary: 'Failed to update survey title. Please try again.',
      }));
      ErrorLogService.logError(updateError ?? 'Update survey title error', 'SurveyResultsSidebarHeader', 'useEffect-isUpdateError');
    }
  }, [isUpdateError, updateError, dispatch]);

  const handleBack = () => {
    navigate('/surveys');
  };

  const handleEditClick = () => {
    if (survey) {
      setIsEditing(true);
      setEditValue(survey.title);
    }
  };

  const handleSaveClick = async() => {
    if (!survey || editValue.trim() === '') {
      setIsEditing(false);
      return;
    }
    await updateSurvey({
      title: editValue.trim(),
      ext_survey_id: survey.extSurveyId,
    });
    setIsEditing(false);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      void handleSaveClick();
    }
  };

  if (!survey) {
    return (
      <Layout
        onBack={handleBack}
        download={<ResultsSidebarHeaderDownload variant="skeleton" />}
        share={<ResultsSidebarHeaderShare variant="skeleton" />}
        title={(
          <div className="flex flex-col gap-1">
            <Skeleton className="h-[38px]" />
            <Skeleton className="h-[38px]" />
          </div>
        )}
        survey={survey ?? undefined}
        createSummary={createSummary}
        createMediaPlan={createMediaPlan}
        isShare={isShare}
      />
    );
  }

  const canDownload = survey.surveyStatus === SurveyStatus.Complete || survey.surveyStatus === SurveyStatus.Live;
  const downloadProps: ResultsSidebarHeaderDownloadProps = canDownload
    ? { variant: "download", isShare, surveyId, title: survey.title }
    : { variant: "cannotDownload" };

  const shareProps: ResultsSidebarHeaderShareProps = canShare
    ? { variant: "share", handleShare, isLoading }
    : { variant: "cannotShare" };

  return (
    <Layout
      onBack={handleBack}
      download={<ResultsSidebarHeaderDownload {...downloadProps} />}
      share={<ResultsSidebarHeaderShare {...shareProps} />}
      title={(
        <div className="flex items-center gap-2">
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={handleEditChange}
              onKeyDown={handleKeyDown}
              className="w-full rounded-md border-none bg-transparent text-2xl focus:outline-none text-ellipsis"
              autoFocus
            />
          ) : (
            <span className="text-2xl truncate max-w-full">
              {survey.title}
            </span>
          )}
          {!isShare && (
            <button
              onClick={isEditing ? handleSaveClick : handleEditClick}
              className="flex items-center justify-center size-8 hover:opacity-80 transition-opacity"
            >
              {isEditing ? (
                <CheckIcon className="size-4 fill-black/75" />
              ) : (
                <PencilIcon className="size-4 fill-black/75" />
              )}
            </button>
          )}
        </div>
      )}
      survey={survey}
      // complete={<ResultsSidebarHeaderComplete {...completeProps} />}
      createSummary={createSummary}
      createMediaPlan={createMediaPlan}
      isShare={isShare}
    />
  );
};
